import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  Grid,
  Typography,
  Popover,
  Button,
  FormControlLabel,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useParams } from 'react-router-dom';
import useApi from 'hooks/useApi';
import DeleteField from '../../otherComponent/DeleteStep';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Reportform from '../../Reportform';

interface CheckboxComponentProps {
  values: any;
  index: number;
  setFieldValue: (key: string, value: any) => void;
  onSubmit: (data: any) => void;
  handleChange: (e: any) => void;
  handleBlur: (e: any) => void;
  selectedNodeData?: any;
  onDeleteField?: (fieldIndex: number) => void;
  toSentenceCase: (str: string) => string;
  fieldTypeSelector?: ReactNode;
  isSettingsOpen?: boolean;
  onSettingsOpen?: (fieldIndex: number) => void;
  onSettingsClose?: () => void;
}

export default function CheckboxComponent({
  values,
  index,
  setFieldValue,
  onSubmit,
  handleChange,
  handleBlur,
  selectedNodeData,
  onDeleteField,
  toSentenceCase,
  fieldTypeSelector,
  isSettingsOpen,
  onSettingsOpen,
  onSettingsClose
}: CheckboxComponentProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [newOption, setNewOption] = useState('');
  const settingsButtonRef = useRef<HTMLDivElement | null>(null);

  // Dynamic field: pull steps + their fields from the same process (same source as rule engine)
  const { teamUid } = useParams<{ teamUid: string }>();
  const { getFlowByTeamUid } = useApi();
  const [flowSteps, setFlowSteps] = useState<any[]>([]);

  useEffect(() => {
    if (typeof isSettingsOpen !== 'boolean') {
      return;
    }

    if (!isSettingsOpen) {
      setAnchorEl(null);
      return;
    }

    if (settingsButtonRef.current) {
      setAnchorEl(settingsButtonRef.current);
    }
  }, [isSettingsOpen]);

  // Load steps of the current process once settings popover opens
  useEffect(() => {
    if (!anchorEl || !teamUid || flowSteps.length > 0) {
      return;
    }
    let active = true;
    (async () => {
      try {
        const response = await getFlowByTeamUid(teamUid);
        if (active && response?.success && response?.data?.steps) {
          setFlowSteps(response.data.steps);
        }
      } catch (error) {
        console.error('Failed to fetch flow steps for dynamic field:', error);
      }
    })();
    return () => {
      active = false;
    };
  }, [anchorEl, teamUid]);

  const handleEditClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onSettingsOpen?.(index);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    onSettingsClose?.();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'edit-checkbox-popover' : undefined;

  const options = values.fields[index]?.options || ['Option 1', 'Option 2'];
  const selectedValues = values.fields[index]?.answer || [];
  const settings = values.fields[index]?.settings || {};
  const orientation = settings.orientation || 'vertical';

  // Dynamic field config stored on the field model:
  // settings.dynamicFields = [{ step: <stepUid>, field: <fieldUid> }, ...]
  const isDynamicField = settings.isDynamicField || false;
  const dynamicFields: { step: string; field: string }[] =
    Array.isArray(settings.dynamicFields) && settings.dynamicFields.length > 0
      ? settings.dynamicFields
      : [{ step: '', field: '' }];

  const getStepFields = (stepUid: string) =>
    flowSteps.find((step: any) => step.uid === stepUid)?.component?.fields || [];

  const updateDynamicFields = (next: { step: string; field: string }[]) =>
    setFieldValue(`fields.${index}.settings.dynamicFields`, next);

  const handleDynamicStepChange = (rowIndex: number, stepUid: string) => {
    const next = dynamicFields.map((row, i) =>
      i === rowIndex ? { step: stepUid, field: '' } : row
    );
    updateDynamicFields(next);
  };

  const handleDynamicFieldChange = (rowIndex: number, fieldUid: string) => {
    const next = dynamicFields.map((row, i) =>
      i === rowIndex ? { ...row, field: fieldUid } : row
    );
    updateDynamicFields(next);
  };

  const addDynamicRow = () =>
    updateDynamicFields([...dynamicFields, { step: '', field: '' }]);

  const removeDynamicRow = (rowIndex: number) =>
    updateDynamicFields(dynamicFields.filter((_, i) => i !== rowIndex));

  const handleCheckboxChange = (option: string) => {
    const newSelected = selectedValues.includes(option)
      ? selectedValues.filter((val: string) => val !== option)
      : [...selectedValues, option];
    setFieldValue(`fields.${index}.answer`, newSelected);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                flexGrow: 1,
                maxWidth: 'calc(100% - 40px)',
                py: 1,
                px: 1,
                border: '1px solid #ccc',
                borderRadius: 1,
                backgroundColor: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minWidth: 0
              }}
            >
              <Typography
                variant="body1"
                sx={{ flexGrow: 1, mr: 1, minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
              >
                {toSentenceCase(values.fields[index].question || 'Untitled Field')}
              </Typography>
              <CheckBoxIcon fontSize="small" sx={{ color: 'gray', flexShrink: 0 }} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                ref={settingsButtonRef}
                sx={{ cursor: 'pointer', color: 'primary.main', display: 'flex', alignItems: 'center' }}
                onClick={handleEditClick}
              >
                <SettingsIcon fontSize="medium" />
              </Box>
              <DeleteField 
                onDeleteField={() => onDeleteField?.(index)}
                selectedNodeData={selectedNodeData}
                fieldIndex={index}
                fieldData={values.fields[index]}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12 }hidden>
          <Box display="flex" flexDirection={orientation === 'horizontal' ? 'row' : 'column'} gap={1}>
            {options.map((option: string, i: number) => (
              <FormControlLabel
                key={i}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedValues.includes(option)}
                    onChange={() => handleCheckboxChange(option)}
                  />
                }
                label={option}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ sx: { width: 350, p: 2 } }}
      >
        <Box>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2} gap={1}>
            <Typography variant="subtitle1">Checkbox Settings</Typography>
            {fieldTypeSelector ? (
              <Box sx={{ minWidth: 120 }}>
                {fieldTypeSelector}
              </Box>
            ) : null}
          </Box>

          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Label"
                name={`fields.${index}.question`}
                defaultValue={values.fields[index].question}
                onChange={handleChange}
                onBlur={handleBlur}
                size="small"
              />
            </Grid>

            {/* <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name={`fields.${index}.description`}
                defaultValue={values.fields[index].description}
                onChange={handleChange}
                onBlur={handleBlur}
                size="small"
              />
            </Grid> */}

            <Grid item xs={12}>
              <Typography variant="body2" fontWeight="bold" mt={1}>
                Options
              </Typography>

              {options.map((opt: string, i: number) => (
                <Box key={i} display="flex" alignItems="center" mt={1}>
                  <TextField
                    fullWidth
                    size="small"
                    value={opt}
                    onChange={(e) => {
                      const updatedOptions = [...options];
                      updatedOptions[i] = e.target.value;
                      setFieldValue(`fields.${index}.options`, updatedOptions);
                    }}
                  />
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      const updatedOptions = options.filter((_: any, idx: number) => idx !== i);
                      setFieldValue(`fields.${index}.options`, updatedOptions);
                    }}
                    sx={{ ml: 1 }}
                  >
                    Delete
                  </Button>
                </Box>
              ))}

              <Box mt={2} display="flex">
                <TextField
                  fullWidth
                  size="small"
                  placeholder="New Option"
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    if (newOption.trim() !== '') {
                      setFieldValue(`fields.${index}.options`, [...options, newOption.trim()]);
                      setNewOption('');
                    }
                  }}
                  sx={{ ml: 1 }}
                >
                  Add
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} sx={{ mt: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={orientation === 'horizontal'}
                    onChange={(e) =>
                      setFieldValue(
                        `fields.${index}.settings.orientation`,
                        e.target.checked ? 'horizontal' : 'vertical'
                      )
                    }
                  />
                }
                label="Horizontal Orientation"
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: -3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.fields[index].settings?.required || false}
                    onChange={(e) =>
                      setFieldValue(`fields.${index}.settings.required`, e.target.checked)
                    }
                  />
                }
                label="Required"
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: -3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDynamicField}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setFieldValue(`fields.${index}.settings.isDynamicField`, isChecked);
                      if (!isChecked) {
                        setFieldValue(`fields.${index}.settings.dynamicFields`, []);
                      } else if (!Array.isArray(settings.dynamicFields) || settings.dynamicFields.length === 0) {
                        setFieldValue(`fields.${index}.settings.dynamicFields`, [{ step: '', field: '' }]);
                      }
                    }}
                  />
                }
                label="Dynamic Field"
              />
            </Grid>

            {isDynamicField && (
              <>
                {dynamicFields.map((row, rowIndex) => {
                  const rowStepFields = getStepFields(row.step);
                  return (
                    <Grid item xs={12} key={rowIndex} sx={{ mt: rowIndex === 0 ? -2 : 0 }}>
                      <Box
                        sx={{
                          border: '1px solid #eee',
                          borderRadius: 1,
                          p: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">
                            Mapping {rowIndex + 1}
                          </Typography>
                          {dynamicFields.length > 1 && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => removeDynamicRow(rowIndex)}
                            >
                              Remove
                            </Button>
                          )}
                        </Box>

                        <FormControl fullWidth size="small">
                          <InputLabel>Step</InputLabel>
                          <Select
                            label="Step"
                            value={row.step || ''}
                            onChange={(e) => handleDynamicStepChange(rowIndex, e.target.value)}
                          >
                            {flowSteps.map((step: any) => (
                              <MenuItem key={step.uid} value={step.uid}>
                                {step.title}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small" disabled={!row.step}>
                          <InputLabel>Field</InputLabel>
                          <Select
                            label="Field"
                            value={row.field || ''}
                            onChange={(e) => handleDynamicFieldChange(rowIndex, e.target.value)}
                          >
                            {rowStepFields.map((f: any, fieldIndex: number) => (
                              <MenuItem key={f.uid || fieldIndex} value={f.uid || f.question}>
                                {f.question}
                              </MenuItem>
                            ))}
                            {rowStepFields.length === 0 && (
                              <MenuItem disabled>
                                {row.step ? 'No fields in this step' : 'Select a step first'}
                              </MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      </Box>
                    </Grid>
                  );
                })}

                <Grid item xs={12} sx={{ mt: -1 }}>
                  <Button size="small" variant="outlined" onClick={addDynamicRow}>
                    Add more
                  </Button>
                </Grid>
              </>
            )}
{/*
            <Grid item xs={12} sx={{ mt: -3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.fields[index].settings?.isSupportFileRequired || false}
                    onChange={(e) =>
                      setFieldValue(`fields.${index}.settings.isSupportFileRequired`, e.target.checked)
                    }
                  />
                }
                label="Support File Required"
              />
            </Grid> */}

            {values.fields[index].settings?.isSupportFileRequired && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name={`fields.${index}.settings.noOfSupportImages`}
                  type="number"
                  label="Number of Images"
                  value={values.fields[index].settings?.noOfSupportImages || ''}
                  onChange={handleChange}
                  size="small"
                  inputProps={{ min: 1 }}
                />
              </Grid>
            )}

            <Grid item xs={12} sx={{ mt: -3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.report?.enabled || false}
                    onChange={(event) => {
                      const isChecked = event.target.checked;
                      setFieldValue(
                        `fields.${index}.settings.report`,
                        isChecked
                          ? { ...(settings.report || {}), enabled: true }
                          : { enabled: false, uid: null, name: null }
                      );
                    }}
                  />
                }
                label="Report "
              />
            </Grid>

            {settings.report?.enabled && (
              <Grid item xs={12} sx={{ mt: -2 }}>
                <Reportform name={`fields.${index}.settings.report`} />
              </Grid>
            )}

            <Grid item xs={12}>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button size="small" variant="contained" onClick={handleClosePopover}>
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Popover>
    </Box>
  );
}
